import React, { useRef, useState } from "react";
import { FileUpload, FileUploadHeaderTemplateOptions, FileUploadSelectEvent, ItemTemplateOptions} from "primereact/fileupload";
import { ProgressBar } from "primereact/progressbar";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import "./imageTemplateDemo.css";
import { Toast } from "primereact/toast";

interface ImagePanelProps {
    onFileUpload: (files: File[]) => void;
    handelFileSize:(message:string) => void
}

export const ImageTemplateDemo: React.FC<ImagePanelProps> = ({ onFileUpload ,handelFileSize}) => {
    const [totalSize, setTotalSize] = useState(0);
    const [fileData, setFileData] = useState<File[]>([]);
    const [hoveredField, setHoveredField] = useState<string | null>(null);
    const fileUploadRef = useRef<FileUpload>(null);
    const toast = useRef<Toast>(null);
    const MAX_TOTAL_SIZE = 2 * 1024 * 1024; // 2MB in bytes
    const today = new Date();
    const formattedDate = `${String(today.getDate()).padStart(2, '0')}/${String(today.getMonth() + 1).padStart(2, '0')}/${String(today.getFullYear()).slice(-2)}`;

    const onTemplateSelect = (e: FileUploadSelectEvent) => {

    const newFiles: File[] = Array.from(e.files);

        // Identify duplicates
    const duplicates: File[] = newFiles.filter((newFile) =>
        fileData.some(
            (existingFile) =>
                existingFile.name === newFile.name &&
                existingFile.size === newFile.size
        )
    );

    if (duplicates.length > 0) {
        // Build a list of duplicate names for the message
        const duplicateNames = duplicates.map((file) => file.name).join(", ");
        handelFileSize(`Duplicate file(s): ${duplicateNames} already selected.`);
        
        return;
    }

    // Combine with existing files, ensuring no duplicates
    const updated = [...fileData, ...newFiles].filter(
        (file, index, self) =>
            index === self.findIndex(
                (f) => f.name === file.name && f.size === file.size
            )
    );

    // Calculate new total size
    const newTotalSize = updated.reduce((sum, file) => sum + file.size, 0);

    // Check if total size exceeds the limit
    if (newTotalSize > MAX_TOTAL_SIZE) {
        // Show an error or toast notification
            handelFileSize("Total size of selected files must be under 2MB.")
        // Prevent upload: remove newly selected files from UI
        if (fileUploadRef.current) {
            fileUploadRef.current.clear(); // This clears all files visually
        }
        return; // Don't update the state
    }
    
    // If within limit, proceed to update
    setFileData(updated);
    setTotalSize(newTotalSize);
    onFileUpload(updated);
};


    const onTemplateRemove = (file: File, callback: Function) => {
            setFileData((prev) => {
            const updatedFiles = prev.filter((f) => f !== file);
            const newTotalSize = updatedFiles.reduce((sum, file) => sum + file.size, 0);
            setTotalSize(newTotalSize);
            onFileUpload(updatedFiles); // Notify the parent with the updated file list
            callback();
            return updatedFiles;
        });
    };

    // When "Clear All" is clicked
    const onTemplateClear = () => {
        setTotalSize(0);
        setFileData([]);
        onFileUpload([]); // ✅ empty list to parent
    };

    const headerTemplate = (options: FileUploadHeaderTemplateOptions) => {
    const { className, chooseButton, cancelButton } = options;
    const value = (totalSize / 2000000) * 100; // progress percentage (max 2MB)
    const formattedValue = fileUploadRef.current?.formatSize(totalSize) || "0 B";

    return (
        <div
        className={className}
        style={{
            backgroundColor: "transparent",
            display: "flex",
            alignItems: "center",
        }}
        >
        {chooseButton}
        {cancelButton}
        <div className="flex align-items-center gap-3 ml-auto">
            <span>{formattedValue} / 2 MB</span>
            <ProgressBar
            value={value}
            showValue={false}
            style={{ width: "10rem", height: "12px" }}
            />
        </div>
        </div>
    );
    };


    // Only for rendering file item
    const itemTemplate = (inFile: object, props: ItemTemplateOptions) => {

        const file = inFile as File;
        const url = URL.createObjectURL(file);

        return (
            <div className="file-item-container">
                <Toast ref={toast} />
                <div className="file-left">
                <img alt={file.name} role="presentation" src={url} width={80} />
                <div className="file-info">
                    <span>{file.name}</span>
                    <small>{formattedDate}</small>
                </div>
                </div>
                <div className="file-meta">
                    <Tag value={props.formatSize} severity="warning" className="px-3 py-2" />
                    <Button
                        type="button"
                        icon="pi pi-times"
                        className="p-button-outlined p-button-rounded p-button-danger custom-remove-btn"
                        onClick={() => onTemplateRemove(file, props.onRemove)}
                    />
                </div>
             </div>
        );
    };

    const emptyTemplate = () => {
        return (
            <div className="flex align-items-center flex-column">
                <i
                    className="pi pi-image mt-3 p-5"
                    style={{
                        fontSize: "5em",
                        borderRadius: "50%",
                        backgroundColor: "var(--surface-b)",
                        color: "var(--surface-d)",
                    }}
                />
                <span
                    style={{ fontSize: "1.2em", color: "var(--text-color-secondary)" }}
                    className="my-5"
                >
                    Drag and Drop Image Here
                </span>
            </div>
        );
    };

    const chooseOptions = {
        icon: "pi pi-fw pi-images",
        iconOnly: true,
        className: "custom-choose-btn p-button-rounded p-button-outlined",
    };
    const cancelOptions = {
        icon: "pi pi-fw pi-times",
        iconOnly: true,
        className: "custom-cancel-btn p-button-danger p-button-rounded p-button-outlined",
    };

    const fieldHoverStyle = (fieldName: string) => ({
        border:
            hoveredField === fieldName ? "2px solid #6366F1" : "1px solid #ced4da",
        borderRadius: "6px",
        transition: "border 0.2s ease-in-out",
        width: "100%",
        minHeight: "40px",
    });

    return (
        <div className="col-12">
            <div className="field mb-3 px-2">
                <label htmlFor="image" style={{ fontWeight: "bold" }}>
                    Document
                </label>
                <div
                    onMouseEnter={() => setHoveredField("image")}
                    style={fieldHoverStyle("image")}
                >
                    <FileUpload
                        ref={fileUploadRef}
                        name="demo[]"
                        url="/api/upload"
                         multiple
                        // maxFileSize={2000000}
                        onSelect={onTemplateSelect}
                        onError={onTemplateClear}
                        onClear={onTemplateClear}
                        headerTemplate={headerTemplate}
                        itemTemplate={itemTemplate}
                        emptyTemplate={emptyTemplate}
                        chooseOptions={chooseOptions}
                        cancelOptions={cancelOptions}
                    />
                </div>
            </div>
        </div>
    );
};
